import React from 'react';
import { Link } from 'react-router-dom';
import urljoin from 'url-join';

import { useStyles } from '~/assets/styles';
import type { ClaimSidebarTabProps } from '~/components/ClaimPage/types';
import mixpanel from '~/components/CmsMain/mixpanel';
import Button from '~/components/core/Atomic/Buttons/Button';
import Tooltip from '~/components/core/Atomic/Tooltip';
import PermissionsRenderWrapper from '~/components/core/Permissions/PermissionsRenderWrapper';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import cn from '~/Utils/cn';

export const ClaimSidebarTab: React.FC<ClaimSidebarTabProps> = ({
  label,
  IconComponent,
  url,
  isActive,
  match,
  claim,
  permissions,
  LabelComponent,
  labelComponentProps,
  shouldReloadLegalActions,
  reloadLegalActions,
  IconProps = {},
}) => {
  const { classes } = useStyles();

  // Each tab getter pairs its LabelComponent with that component's own props, but the two arrive
  // here as independent unions and TS cannot re-correlate them across a JSX spread. The pairing is
  // enforced where it is built, by the per-key conditional types on ClaimSideBarTab.
  const TabLabelComponent = LabelComponent as
    | React.ComponentType<NonNullable<ClaimSidebarTabProps['labelComponentProps']>>
    | undefined;

  const handleClick = async () => {
    mixpanel?.track(MIXPANEL_EVENTS.CLAIM_PANEL_CLICK, {
      element: label,
      claim_id: claim?.id,
    });
    if (shouldReloadLegalActions) {
      await reloadLegalActions();
    }
  };

  const tabButton = (
    <>
      <div className={classes.displayUpMd}>
        <Button
          // `claimMenuButtonLabel` used to arrive via `classes.label`; v5 deleted that slot, so it
          // moves to the root. Everything it sets is inherited typography, so the label still gets it.
          className={cn(classes.claimMenuButton, classes.claimMenuButtonLabel, {
            [classes.claimMenuButtonSelected]: isActive,
            [classes.claimMenuButtonLabelSelected]: isActive,
          })}
          component={Link}
          to={urljoin(match.url, url)}
          disableRipple
          startIcon={
            <IconComponent
              {...IconProps}
              className={cn({ [classes.claimMenuIconSelected]: isActive }, IconProps.className)}
            />
          }
          onClick={handleClick}
        >
          {TabLabelComponent && labelComponentProps ? <TabLabelComponent {...labelComponentProps} /> : label}
        </Button>
      </div>

      <div className={classes.displayDownLg}>
        <Tooltip title={label} placement="right" arrow>
          <Button
            className={cn(classes.claimMenuButton, classes.claimMenuButtonLabel, {
              [classes.claimMenuButtonSelected]: isActive,
              [classes.claimMenuButtonLabelSelected]: isActive,
            })}
            component={Link}
            from={`${match.path}/`}
            to={urljoin(match.url, url)}
            disableRipple
            startIcon={
              <IconComponent
                {...IconProps}
                className={cn({ [classes.claimMenuIconSelected]: isActive }, IconProps.className)}
              />
            }
            onClick={handleClick}
          />
        </Tooltip>
      </div>
    </>
  );

  return permissions ? (
    <PermissionsRenderWrapper verb={permissions.verb} action={permissions.action}>
      {tabButton}
    </PermissionsRenderWrapper>
  ) : (
    tabButton
  );
};
